"use client";
import { useLayoutEffect, useRef, type JSX } from "react";
import { Badge } from "@/components/ui/badge";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
import { motion, type Variants } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

type FormationStep = {
  step: string;
  title: string;
  description: string[];
  side: "left" | "right";
};

const formationSteps: FormationStep[] = [
  {
    step: "Step 01",
    title: "Start With a Short Review",
    description: [
      "Tell us about your company, your financial year end and where your records currently sit. From there we move to WhatsApp, so you are speaking with our team directly rather than a form.",
    ],
    side: "left",
  },
  {
    step: "Step 02",
    title: "We Review What You Already Have",
    description: [
      "If books already exist, our team checks them and tells you plainly what needs correcting before we take over. If nothing has been kept so far, we tell you exactly what to gather first.",
    ],
    side: "right",
  },
  {
    step: "Step 03",
    title: "We Prepare Your Accounts",
    description: [
      "Your transactions are reconciled, your financial statements are prepared and your dashboard is kept current with your monthly performance and full transaction listing.",
    ],
    side: "left",
  },
  {
    step: "Step 04",
    title: "Your Accounts Are Reviewed and Finalised",
    description: [
      "Before anything is called finished, your accounts go through review by our department head and senior lead, so nothing reaches your auditor or your tax filing half checked.",
    ],
    side: "right",
  },
  {
    step: "Step 05",
    title: "You Are Handed Accounts Ready for What Comes Next",
    description: [
      "Whether that is audit, tax filing or simply understanding your own numbers, your accounts are already in a state that does not need to be rebuilt by whoever picks them up next.",
    ],
    side: "left",
  },
];

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// The pinned layout only makes sense with the two-column width *and* enough
// viewport height to hold a step. Both halves of the implementation read from
// here: the `pinned:` Tailwind screen (tailwind.config.ts) applies the stage
// markup, and this query builds the animation for it. They must stay identical
// — the fixed-height stage with nothing scrolling it would show one step only.
const PINNED_QUERY =
  "(min-width: 768px) and (min-height: 640px) and (prefers-reduced-motion: no-preference)";
// Everything else that still animates: phones, and short/landscape windows
// wide enough to hit `md` but too short to pin. Written as two queries rather
// than one `or` so it does not depend on Media Queries Level 4 parsing.
const FLOW_QUERIES = [
  "(max-width: 767px) and (prefers-reduced-motion: no-preference)",
  "(min-width: 768px) and (max-height: 639px) and (prefers-reduced-motion: no-preference)",
];

// Marker states. A step is dim before the progress line reaches it, briefly
// emphasised while it is the current milestone, then settles back to a
// "completed" look once the line moves on — so exactly one marker ever reads
// as *now*, instead of all five looking equally active by the end.
//
// Dimming is done with `filter: brightness()`, not `opacity`. The marker's
// badge has an opaque background and sits in front of the progress line, but
// CSS `opacity` composites the *whole* element against whatever is behind
// it — so a dimmed marker let the line underneath show straight through its
// supposedly-solid background. `brightness()` only rescales the marker's own
// rendered pixels; the badge stays fully opaque and keeps blocking the line
// at every state.
const MARKER_IDLE = { opacity: 1, scale: 0.9, filter: "brightness(0.45)" } as const;
const MARKER_ACTIVE = { opacity: 1, scale: 1.06, filter: "brightness(1)" } as const;
const MARKER_DONE = { opacity: 1, scale: 1, filter: "brightness(0.85)" } as const;

const CONTENT_HIDDEN = { opacity: 0, y: 28 } as const;
const CONTENT_SHOWN = { opacity: 1, y: 0 } as const;

// One timeline unit = one step-to-step transition. `travel` is the share of
// that unit the progress line spends moving; the marker lands as the line
// arrives and the copy follows just behind it, so each reveal reads as *caused
// by* the line rather than as its own fade-in. Offsets are fractions of a unit.
type Beats = {
  travel: number;
  prevSettle: number;
  markerLand: number;
  content: number;
};

// Pinned: the list is translated by the same tween window as the line, so the
// line may finish early and dwell — that pause is the beat each step gets.
const PINNED_BEATS: Beats = {
  travel: 0.7,
  prevSettle: 0.45,
  markerLand: 0.62,
  content: 0.68,
};
// Flow: the page itself scrolls the list, so the line has to fill across the
// *whole* segment. Any dwell and its tip would drift away from the marker it
// is supposed to be arriving at, which is the illusion the whole thing rests
// on — hence the later, tighter reveal offsets.
const FLOW_BEATS: Beats = {
  travel: 1,
  prevSettle: 0.5,
  markerLand: 0.78,
  content: 0.86,
};

// Trailing pause so step 05 is fully settled before the section unpins.
const END_HOLD = 0.35;
// Where the "current step" line sits — the height the progress line's tip
// holds while the milestones travel into it. Well above halfway, because a
// step's copy is top-aligned with its marker and flows *downwards* from it:
// centring the marker would push the longest step (02, two paragraphs) past
// the bottom of the stage, and leave a void under the heading at step 01.
const PINNED_READ_LINE = 0.26;
// The same idea for the un-pinned layout, as a fraction of the viewport.
const FLOW_READ_LINE = 0.45;

// Scroll distance one transition consumes while pinned. Proportional to the
// viewport so a large monitor gets more room, with a floor for short windows.
const scrollPerTransition = () => Math.max(window.innerHeight * 0.7, 520);

/** Offset of `el`'s top edge from `ancestor`'s. Unaffected by CSS transforms. */
const offsetTopWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let top = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    top += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return top;
};

const TimelineMarker = ({ step }: { step: string }) => (
  <Badge
    className="relative z-10 flex h-auto min-w-[76px] justify-center overflow-hidden rounded-[50px] border border-white bg-[#0f1218] px-2.5 py-2.5 [box-shadow:inset_0px_2px_4px_#ffffffe6] hover:bg-[#0f1218] "
    variant="outline"
  >
    <span className="pointer-events-none absolute  sm:left-[-121px] sm:top-[34px] bottom-0 h-[15px] sm:h-[133px] w-[50px]  sm:w-[280px] rounded-[139.96px/66.62px] bg-[#0061fe] blur-[14.52px]" />
    <span className="pointer-events-none absolute left-[calc(50%_+_10px)] top-[-26px] h-[41px] w-[41px] rounded-[20.51px] bg-[#6fa6ff] blur-[17.18px]">
      <span className="absolute left-[calc(50%_-_21px)] top-0 h-[25px] w-[41px] rounded-[20.51px/12.61px] bg-[#d2e3ff] blur-[17.18px]" />
      <span className="absolute left-[calc(50%_-_21px)] top-0 h-[21px] w-[41px] rounded-[20.51px/10.67px] bg-white blur-[8.87px]" />
    </span>
    <span className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] relative whitespace-nowrap text-base font-normal leading-6 text-white">
      {step}
    </span>
  </Badge>
);

export const CompanyFormationTimelineSection = (): JSX.Element => {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const markerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // useLayoutEffect (not useEffect): this creates a ScrollTrigger `pin`, which
  // reparents the pinned DOM node into a GSAP-created `.pin-spacer` div,
  // outside of React's knowledge. useEffect's cleanup for an unmounting
  // component runs asynchronously, *after* React has already removed the old
  // DOM subtree from its recorded parent — but if the pin-spacer is still in
  // place at that point, the real parent no longer matches what React
  // expects, and `parent.removeChild(node)` throws "The node to be removed
  // is not a child of this node." useLayoutEffect's cleanup runs
  // synchronously as part of the same commit, before that removal, so
  // ScrollTrigger has already unwrapped the spacer by the time React touches
  // the DOM.
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    const list = listRef.current;
    const track = trackRef.current;
    const progress = progressRef.current;
    const contents = contentRefs.current;
    const markers = markerRefs.current.filter(
      (el): el is HTMLDivElement => el !== null,
    );

    if (!section || !stage || !list || !track || !progress) return;
    if (markers.length !== formationSteps.length) return;

    gsap.registerPlugin(ScrollTrigger);

    // ScrollTrigger auto-refreshes on window "resize" by default, which
    // re-evaluates this section's function-based `start` (the flow branch's
    // readLine() reads window.innerHeight). Mobile browsers fire that same
    // "resize" event purely from their address bar collapsing/expanding
    // while the page scrolls, with no real layout change — each one was
    // silently re-anchoring the progress line to a different scroll offset
    // mid-scroll, snapping it away from the step actually on screen. Our own
    // watchForReflow already rebuilds on a genuine width change, so drop
    // "resize" from ScrollTrigger's own auto-refresh triggers.
    ScrollTrigger.config({
      autoRefreshEvents: "DOMContentLoaded,load,visibilitychange",
    });

    let cancelled = false;

    // Both branches below are scoped to "motion is allowed". Under a
    // reduced-motion preference neither runs, so what renders is the plain
    // markup: every step at full opacity and the progress line drawn its full
    // length by its own CSS. The `pinned:` classes are gated on the same
    // preference, so the section also stays an ordinary, un-pinned list.
    const mm = gsap.matchMedia();

    /**
     * Each marker's centre as a distance from the top of the list, measured
     * live rather than hard-coded: the steps have differing paragraph counts,
     * so the centres move whenever the copy reflows (mount, font swap, resize).
     * Also sizes both line layers to span marker-01's centre to marker-05's,
     * which is what makes the progress line's scaleY mean "how far through the
     * process are we".
     */
    const measure = () => {
      const centres = markers.map(
        (marker) => offsetTopWithin(marker, list) + marker.offsetHeight / 2,
      );
      const span = centres[centres.length - 1] - centres[0];

      gsap.set([track, progress], {
        top: centres[0],
        height: span,
        bottom: "auto",
      });
      // The line is centred on the axis with a Tailwind `-translate-x-1/2`,
      // which GSAP's own transform would otherwise drop once it writes scaleY.
      gsap.set(progress, { xPercent: -50, transformOrigin: "top center" });

      return {
        centres,
        fractionAt: (index: number) =>
          span > 0 ? (centres[index] - centres[0]) / span : 0,
      };
    };

    const resetToInitialState = () => {
      gsap.set(progress, { scaleY: 0 });
      markers.forEach((marker, index) => {
        gsap.set(marker, index === 0 ? MARKER_ACTIVE : MARKER_IDLE);
      });
      contents.forEach((content, index) => {
        if (content) {
          gsap.set(content, index === 0 ? CONTENT_SHOWN : CONTENT_HIDDEN);
        }
      });
    };

    /**
     * The previous marker settles, this one lands, then its copy arrives.
     * Offsets and durations are scaled by `unit` so a segment that covers a
     * shorter stretch of the line plays its reveal proportionally faster.
     */
    const addReveal = (
      timeline: gsap.core.Timeline,
      index: number,
      base: number,
      unit: number,
      beats: Beats,
    ) => {
      timeline.to(
        markers[index - 1],
        { ...MARKER_DONE, duration: 0.3 * unit, ease: "power1.out" },
        base + beats.prevSettle * unit,
      );
      timeline.to(
        markers[index],
        { ...MARKER_ACTIVE, duration: 0.28 * unit, ease: "power2.out" },
        base + beats.markerLand * unit,
      );
      const content = contents[index];
      if (content) {
        timeline.to(
          content,
          { ...CONTENT_SHOWN, duration: 0.4 * unit, ease: "power2.out" },
          base + beats.content * unit,
        );
      }
    };

    // Reverting a `fromTo` restores its *start* value rather than a clean
    // slate, which would leave the list translated and steps 02-05 invisible
    // in the static fallback layout. Every element here is styled by classes
    // alone, so dropping the whole style attribute is safe.
    //
    // It has to happen a frame later, once GSAP's own revert has finished —
    // but a deferred wipe is dangerous when the effect is about to re-run.
    // React Strict Mode mounts, cleans up, then mounts again synchronously,
    // so an unguarded frame-later wipe lands *after* the second mount has
    // written its initial state and strips it, leaving the line drawn full
    // length and every step already visible. `cancelled` distinguishes the
    // two callers: it is already true when the effect is tearing down (the
    // styles are going away with the component either way), and false when
    // gsap.matchMedia reverts a still-mounted component because the viewport
    // crossed the breakpoint — which is the case that genuinely needs it.
    const clearInlineStylesAfterRevert = () => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        [list, track, progress, ...markers, ...contents].forEach((el) => {
          el?.removeAttribute("style");
        });
      });
    };

    /** Debounced rebuild on resize + after the custom fonts swap in. */
    const watchForReflow = (build: () => void) => {
      let resizeTimeout: number | undefined;
      let lastWidth = window.innerWidth;

      const handleResize = () => {
        // Mobile browsers change window.innerHeight alone as their address
        // bar/toolbar collapses and expands mid-scroll — not a real layout
        // change, but it fires a `resize` event just like one. The flow
        // branch's readLine() reads window.innerHeight, so rebuilding on
        // that recomputes the ScrollTrigger's start position without the
        // scrollY itself changing, which silently jumps the drawn progress
        // away from the step actually on screen. Only a width change (real
        // resize, rotation) is a genuine reflow that needs a rebuild.
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;

        window.clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(build, 200);
      };
      window.addEventListener("resize", handleResize);

      // The Suisse faces use font-display: swap, so the copy can still reflow
      // after the first measurement. `document.fonts.ready` is bounded — fast
      // now that the two above-the-fold weights are preloaded in app/layout —
      // unlike window "load", which waits on every asset on the page.
      if (typeof document !== "undefined" && "fonts" in document) {
        document.fonts.ready.then(() => {
          if (!cancelled) build();
        });
      }

      return () => {
        window.clearTimeout(resizeTimeout);
        window.removeEventListener("resize", handleResize);
      };
    };

    // --------------------------------------------------------------- pinned
    // One master timeline driven by one ScrollTrigger. The section pins and
    // the list slides upward so the marker being activated always lands on the
    // stage's read line. Because the line grows and the list travels across
    // the same linear window, the tip of the blue line stays parked on that
    // read line and the milestones move *into* it — the line reads as progress
    // being consumed, not as five unrelated fade-ins.
    mm.add(PINNED_QUERY, () => {
      let timeline: gsap.core.Timeline | null = null;

      const build = () => {
        timeline?.scrollTrigger?.kill();
        timeline?.kill();

        const { centres, fractionAt } = measure();
        resetToInitialState();

        // Read off the stage, which is a flex-1 child of the pinned column:
        // it is whatever height is left under the heading, so this needs no
        // viewport arithmetic and holds at any window size.
        const activeY = stage.clientHeight * PINNED_READ_LINE;
        const yFor = (index: number) => activeY - centres[index];
        const transitions = markers.length - 1;

        gsap.set(list, { y: yFor(0) });

        timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: () =>
              `+=${scrollPerTransition() * (transitions + END_HOLD)}`,
            scrub: 0.4,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        for (let index = 1; index < markers.length; index += 1) {
          const base = index - 1;

          // `fromTo` with explicit start values (rather than `to`) so the
          // timeline resolves to the same state at any scrubbed position, in
          // either direction, however the user arrived there.
          //
          // `immediateRender: false` is load-bearing: a `fromTo` otherwise
          // renders its *from* value the moment it is created, so the last
          // segment built would win and leave the line pre-drawn to step 04
          // and the list pre-scrolled until the trigger first ticked. The
          // pre-start state is owned by resetToInitialState() instead.
          timeline.fromTo(
            progress,
            { scaleY: fractionAt(index - 1) },
            {
              scaleY: fractionAt(index),
              duration: PINNED_BEATS.travel,
              ease: "none",
              immediateRender: false,
            },
            base,
          );
          timeline.fromTo(
            list,
            { y: yFor(index - 1) },
            {
              y: yFor(index),
              duration: PINNED_BEATS.travel,
              ease: "none",
              immediateRender: false,
            },
            base,
          );

          addReveal(timeline, index, base, 1, PINNED_BEATS);
        }

        timeline.to({}, { duration: END_HOLD });

        ScrollTrigger.refresh();
      };

      build();
      const stopWatching = watchForReflow(build);

      return () => {
        stopWatching();
        timeline?.scrollTrigger?.kill();
        timeline?.kill();
        clearInlineStylesAfterRevert();
      };
    });

    // ----------------------------------------------------------------- flow
    // Pinning a viewport-height stage is a poor trade on phones and short
    // landscape windows (dynamic browser chrome, taller copy, less patience),
    // so the page's own scroll drives the sequence instead. Still one master
    // timeline on one ScrollTrigger, not one per step: several scrubbed
    // triggers writing the same `scaleY` fight over it whenever their ranges
    // overlap, which left the line's fill depending on whether you had arrived
    // from above or below.
    //
    // Each segment's duration is proportional to the gap it spans, and the
    // trigger consumes exactly that many pixels of scroll — so the tip of the
    // line advances 1:1 with the page and stays level with the read line,
    // reaching each marker exactly as that marker crosses it.
    const addFlowSequence = () => {
      let timeline: gsap.core.Timeline | null = null;

      const build = () => {
        timeline?.scrollTrigger?.kill();
        timeline?.kill();

        const { centres, fractionAt } = measure();
        resetToInitialState();

        const span = centres[centres.length - 1] - centres[0];
        const transitions = markers.length - 1;
        const averageGap = span / transitions;
        const readLine = () =>
          Math.max(0, window.innerHeight * FLOW_READ_LINE - centres[0]);

        timeline = gsap.timeline({
          scrollTrigger: {
            trigger: list,
            start: () => `top ${Math.round(readLine())}px`,
            end: () => `+=${Math.round(span)}`,
            scrub: 0.4,
            invalidateOnRefresh: true,
          },
        });

        let base = 0;
        for (let index = 1; index < markers.length; index += 1) {
          const unit = (centres[index] - centres[index - 1]) / averageGap;

          // See the pinned branch: without `immediateRender: false` the last
          // segment's *from* value would paint the line straight away.
          timeline.fromTo(
            progress,
            { scaleY: fractionAt(index - 1) },
            {
              scaleY: fractionAt(index),
              duration: FLOW_BEATS.travel * unit,
              ease: "none",
              immediateRender: false,
            },
            base,
          );

          addReveal(timeline, index, base, unit, FLOW_BEATS);
          base += unit;
        }

        ScrollTrigger.refresh();
      };

      build();
      const stopWatching = watchForReflow(build);

      return () => {
        stopWatching();
        timeline?.scrollTrigger?.kill();
        timeline?.kill();
        clearInlineStylesAfterRevert();
      };
    };

    FLOW_QUERIES.forEach((query) => mm.add(query, addFlowSequence));

    return () => {
      cancelled = true;
      mm.revert();
    };
  }, []);

  return (
    <>
    <section
      ref={sectionRef}
      aria-labelledby="company-formation-timeline-title"
      className="w-full bg-black text-white pinned:flex pinned:h-screen pinned:flex-col pinned:overflow-hidden"
    >
      <div className="mx-auto w-full max-w-[1224px] px-4 pt-16 sm:px-8 pinned:flex pinned:min-h-0 pinned:flex-1 pinned:flex-col pinned:pb-6 pinned:pt-24">
        <header className="flex flex-col items-center gap-4 text-center">
          <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] tracking-[0] text-white backdrop-blur-[3.5px]">
            The process
          </Badge>
          <motion.h2
            id="company-formation-timeline-title"
            className="mt-6 max-w-[937px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-[34px] font-normal leading-[1.15] tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-5xl sm:leading-[64px]"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Get Your Financial Year in Order{" "}
            <br className="hidden sm:block" />
             in a Few Clear Steps
          </motion.h2>
        </header>

        {/* The stage. Only while pinned does it become a fixed-height window
            that the list travels *inside*, taking whatever height is left
            under the heading; the soft top/bottom mask keeps steps from
            clipping hard against its edges. Otherwise it is a plain wrapper
            and the list flows at its natural height. */}
        <div
          ref={stageRef}
          className="relative pinned:mt-6 pinned:min-h-0 pinned:flex-1 pinned:overflow-hidden pinned:[-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_92%,transparent_100%)] pinned:[mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_92%,transparent_100%)]"
        >
          <ol
            ref={listRef}
            className="relative mx-auto mt-24 flex max-w-[796px] flex-col gap-12 pinned:absolute pinned:inset-x-0 pinned:top-0 pinned:mt-0 pinned:will-change-transform"
          >
            {/* Background track, and the blue progress line stacked on top of
                it. Both are sized by JS to span marker-01's centre to
                marker-05's centre; the classes are the static fallback. */}
            <div
              ref={trackRef}
              aria-hidden="true"
              className="pointer-events-none absolute bottom-[185px] left-[38px] top-11 w-[3px] -translate-x-1/2 bg-[#ffffff1f] md:left-1/2"
            />
            <div
              ref={progressRef}
              aria-hidden="true"
              className="pointer-events-none absolute bottom-[185px] left-[38px] top-11 w-[3px] origin-top -translate-x-1/2 bg-[linear-gradient(180deg,rgba(0,97,254,1)_0%,rgba(255,255,255,1)_100%)] will-change-transform md:left-1/2"
            />
            {formationSteps.map((item, index) => {
              const isLeft = item.side === "left";

              return (
                <li
                  className="relative grid grid-cols-[76px_minmax(0,1fr)] items-start gap-2 md:grid-cols-[minmax(0,1fr)_76px_minmax(0,1fr)] md:gap-8"
                  key={item.step}
                >
                  <div
                    ref={(el) => {
                      contentRefs.current[index] = el;
                    }}
                    className={`order-2 flex max-w-[340px] flex-col gap-2 md:order-none md:row-start-1 ${
                      isLeft
                        ? "md:col-start-1 md:items-end md:text-right"
                        : "md:col-start-3 md:items-start md:text-left"
                    }`}
                  >
                    <h3 className="[font-family:'Suisse_Intl_Trial-Bold',Helvetica] text-[18px]  sm:text-xl font-bold leading-7 tracking-[0] text-white">
                      {item.title}
                    </h3>
                    <div className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] space-y-4 text-sm sm:text-base font-normal leading-6 tracking-[0] text-white">
                      {item.description.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                  <div
                    ref={(el) => {
                      markerRefs.current[index] = el;
                    }}
                    className="order-1 flex justify-start md:order-none md:col-start-2 md:row-start-1 md:justify-center"
                  >
                    <TimelineMarker step={item.step} />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
    <div className="flex w-full items-center mt-16 justify-center bg-black px-4 pb-16 text-white sm:px-8 md:-mt-[200px]">
      <button
        type="button"
        onClick={() => openWhatsAppWithUtm(WA_NUMBERS, "accounting-services")}
        className="cursor-pointer relative inline-flex h-auto min-h-12 items-center justify-center whitespace-normal rounded-[40px] border-0 bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] px-6 py-3 text-center [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm font-normal leading-5 tracking-[0] text-white transition-colors hover:bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)]"
      >
        Start My Hong Kong Accounting Service
      </button>
    </div>
    </>
  );
};
